import { Helmet } from "react-helmet";

export const SITE_URL = "https://kre8content.com";
export const DEFAULT_SOCIAL_IMAGE = `${SITE_URL}/logo512.png`;

export function absoluteUrl(path = "/") {
  if (!path) return SITE_URL;
  if (/^https?:\/\//i.test(path)) return path;

  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Kre8 Content",
  url: SITE_URL,
  logo: DEFAULT_SOCIAL_IMAGE,
  description:
    "Kre8 Content is a social media analytics dashboard for creators, streamers, marketers, and campaign managers. Track TikTok video analytics, Instagram Reels performance, and Twitch stream stats to make informed content decisions.",
  sameAs: [
    "https://www.instagram.com/kre8content.comm",
    "https://www.youtube.com/channel/UCQQC7tisQFYewbxfh1ARQ9Q/",
    "https://x.com/Kre8Content",
    "https://www.tiktok.com/@kre8content",
    "https://www.reddit.com/r/kre8content",
  ],
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Kre8 Content",
  url: SITE_URL,
  description:
    "Track TikTok analytics, Instagram Reels analytics, and Twitch stream stats in one social media analytics dashboard.",
};

export function breadcrumbSchema(items = []) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}

export function profilePageSchema({
  name,
  description,
  path,
  image,
  sameAs = [],
  role = "Video creator",
  knowsAbout,
}) {
  const person = {
    "@type": "Person",
    name,
    url: absoluteUrl(path),
    description,
    image,
    jobTitle: role,
    sameAs: sameAs.filter(Boolean),
  };

  if (knowsAbout) {
    person.knowsAbout = Array.isArray(knowsAbout) ? knowsAbout : [knowsAbout];
  }

  return {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    url: absoluteUrl(path),
    name,
    description,
    mainEntity: person,
  };
}

export default function SEO({
  title = "TikTok Analytics & Instagram Reels Tracker | Kre8 Content",
  description = "Track TikTok analytics, Instagram Reels performance, and Twitch stream stats in one social media analytics dashboard to make smarter content decisions.",
  canonicalPath = "/",
  image = DEFAULT_SOCIAL_IMAGE,
  type = "website",
  noindex = false,
  nofollow,
  structuredData = [],
  children,
}) {
  const canonicalUrl = absoluteUrl(canonicalPath);
  const imageUrl = absoluteUrl(image);
  const followDirective = nofollow ?? noindex;
  const robots = noindex
    ? `noindex,${followDirective ? "nofollow" : "follow"}`
    : "index,follow";
  const jsonLd = Array.isArray(structuredData)
    ? structuredData.filter(Boolean)
    : [structuredData].filter(Boolean);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:site_name" content="Kre8 Content" />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={imageUrl} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {jsonLd.map((item, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}

      {children}
    </Helmet>
  );
}
